import React, { useContext, useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Pause, Clock, Info, AlertTriangle } from 'lucide-react';
import { fmtEur, fmtNum } from '../../utils/formatArcaNumbers';
import { RESCUE_REASONS } from '../../utils/rescueDocsCatalog';
import createWithdrawalRequest from '../../api/pension-plan-withdrawals/createWithdrawalRequest';
import {
	WithdrawalKind,
	WithdrawalReason
} from '../../api/pension-plan-withdrawals/interfaces/WithdrawalRequest';
import RescueRequestSubmittedV2 from '../RescueRequestSubmittedV2';
import { translateWithdrawalError } from '../../utils/translateWithdrawalError';
import RescueSupportNoteV2 from '../RescueSupportNoteV2';

/**
 * ⚠️ UX SCAFFOLD (tarea Notion "Definimos MVP rescates") — modal de
 * configuración de la renta mensual, separado de la card de
 * RescueRentaSimulatorV2 (que ahora solo resume el estado actual). No
 * persiste nada real: no existe hoy ningún endpoint ni operación SOAP de
 * escritura de rescate. NO SEGUIR ESTE ARCHIVO COMO REFERENCIA DE CALIDAD —
 * sin tests, sin conexión a datos reales.
 *
 * Sin selector de día (quitado a petición de usuario, jul-2026 — Caser
 * informó que la renta mensual siempre se ejecuta el día 25): se informa
 * como dato fijo, no como campo editable.
 *
 * Sin IBAN por ahora (quitado a petición de usuario, jul-2026): se pedirá
 * más adelante, no aquí — mismo criterio que SolicitarRescatePorMotivoDialogV2.
 *
 * Sin paso de contraseña ni de verificación de identidad (KYC) por ahora
 * (quitados a petición de usuario, jul-2026 — quedará para una tarea de
 * seguridad futura): tanto guardar/reactivar como pausar y cambiar de motivo
 * actúan directamente y pasan a la misma confirmación, que ya avisa de que
 * el equipo contactará para pedir la documentación necesaria — no hace falta
 * un paso adicional para eso.
 */

// Caser ejecuta la renta mensual siempre este día — no es configurable por
// el usuario (informado por Caser, jul-2026).
export const RENTA_MENSUAL_DAY = 25;

// Si faltan estos días o menos para la próxima ejecución, no podemos
// garantizar que una alta/cambio llegue a tiempo para el ciclo en curso.
export const RENTA_ALERT_WINDOW_DAYS = 7;

// Días que faltan hasta la próxima ejecución del día fijo (0 = hoy es el
// día). Si ya pasó este mes, calcula contra el mismo día del mes siguiente.
export function daysUntilNextRentaDay(today: Date = new Date()): number {
	const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	let next = new Date(today.getFullYear(), today.getMonth(), RENTA_MENSUAL_DAY);
	if (next.getTime() < todayMidnight.getTime()) {
		next = new Date(today.getFullYear(), today.getMonth() + 1, RENTA_MENSUAL_DAY);
	}
	const msPerDay = 1000 * 60 * 60 * 24;
	return Math.round((next.getTime() - todayMidnight.getTime()) / msPerDay);
}

export interface RentaMensualConfig {
	amount: number;
	day: number;
	paused: boolean;
}

export interface ConfigurarRentaMensualDialogV2Props {
	isOpen: boolean;
	onClose: () => void;
	availableToRescue: number;
	// Email del usuario que confirma.
	email: string;
	// Importe rescatable SOLO por antigüedad (tramo +10 años) — si se pasa, el
	// máximo se recalcula a este valor en cuanto el motivo elegido en el propio
	// desplegable sea "Antigüedad" (ese motivo, a diferencia de jubilación o
	// incapacidad, no da acceso al resto del saldo). Sin dato real de "tramo
	// +10 años" para este usuario, se omite y el máximo no se reduce.
	seniorityAmount?: number;
	// Configuración actual, si ya hay una renta establecida.
	currentConfig: RentaMensualConfig | null;
	// Cantidad que ya se estaba previsualizando en el slider de la card (antes
	// de abrir el modal) — se usa como valor de partida cuando aún no hay
	// `currentConfig`, para que lo que veías fuera no "salte" al abrir esto.
	initialAmountOverride?: number;
	// Plan del que se rescata (`user_epp_config.id`) y token: sin ellos no se
	// puede registrar la solicitud en backend.
	userEppConfigId?: string | null;
	token?: string | null;
	// Aviso al padre de que hay una solicitud nueva: sin esto su `openRequest`
	// queda obsoleto y los demás puntos de entrada siguen habilitados.
	onSubmitted?: () => void;
	// Motivo ya establecido, si lo hay (ver rescueDocsCatalog.ts). Si es
	// 'retirement' (Jubilación), el campo de motivo queda fijo y no editable
	// — mismo criterio que en RescueRequestDialogV2.
	currentReason?: string | null;
	onSave: (config: RentaMensualConfig) => void;
}

export const MIN_MONTHLY_RENTA = 50;

const ConfigurarRentaMensualDialogV2: React.FC<ConfigurarRentaMensualDialogV2Props> = ({
	isOpen,
	onClose,
	availableToRescue,
	email,
	currentConfig,
	userEppConfigId,
	token,
	onSubmitted,
	initialAmountOverride,
	currentReason,
	seniorityAmount,
	onSave
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const [reason, setReason] = useState(currentReason || 'retirement');
	// Por normativa, "Antigüedad" solo da acceso al tramo +10 años, no al resto
	// del saldo — el resto de motivos (jubilación, incapacidad...) sí liberan
	// todo. Recalcula el máximo cada vez que cambia el motivo elegido, no solo
	// al abrir el modal.
	const effectiveAvailable =
		reason === 'seniority' && seniorityAmount != null
			? Math.min(availableToRescue, seniorityAmount)
			: availableToRescue;

	const sliderMax = Math.max(Math.ceil(effectiveAvailable / 12 / 100) * 100, MIN_MONTHLY_RENTA * 2);
	const defaultAmount = Math.min(
		Math.max(Math.round(effectiveAvailable / 60 / 10) * 10, MIN_MONTHLY_RENTA),
		sliderMax
	);
	const startingAmount = currentConfig?.amount ?? initialAmountOverride ?? defaultAmount;

	const [amount, setAmount] = useState(startingAmount);
	// Se apaga en cuanto la persona toca el slider por primera vez, no solo
	// cuando la animación termina sola (mismo criterio que en
	// SolicitarRescatePorMotivoDialogV2).
	const [hasAdjustedAmount, setHasAdjustedAmount] = useState(false);
	const [paused, setPaused] = useState(currentConfig?.paused ?? false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [step, setStep] = useState<'form' | 'submitted'>('form');

	// Refleja la renta YA GUARDADA (no la que se está a punto de guardar) —
	// decide qué vista mostrar: resumen compacto + "Reactivar" si está pausada,
	// o el formulario completo si está activa.
	const isCurrentlyPaused = currentConfig?.paused ?? false;
	const submittedTitle = paused
		? translate('rescue.rentaDialog.submittedPaused')
		: isCurrentlyPaused
			? translate('rescue.rentaDialog.submittedReactivated')
			: translate('rescue.rentaDialog.submittedSaved');

	// Bloqueado solo si YA hay un rescate hecho por jubilación (currentReason
	// viene del padre, no del valor que se esté seleccionando ahora mismo) —
	// no basta con que el desplegable arranque en "Jubilación" por defecto.
	const isReasonLocked = currentReason === 'retirement';

	// Solo al ABRIR (no en cada cambio de currentConfig mientras isOpen sigue
	// true) — si dependiera de currentConfig, guardar (que actualiza
	// currentConfig desde el padre en el mismo tick) volvía a disparar este
	// efecto y deshacía el setStep('submitted') justo después de guardar, sin
	// que se llegara a ver la confirmación.
	useEffect(() => {
		if (isOpen) {
			setHasAdjustedAmount(false);
			setPaused(currentConfig?.paused ?? false);
			setReason(currentReason || 'retirement');
			setStep('form');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// Si al cambiar de motivo el máximo baja (p. ej. a "Antigüedad"), el
	// importe ya elegido no puede quedar por encima del nuevo tope.
	useEffect(() => {
		setAmount((current) => Math.min(current, sliderMax));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sliderMax]);

	// Solo al ABRIR (mismo motivo que el efecto de arriba: si dependiera de
	// currentConfig, guardar lo cambiaría a mitad de la propia acción de
	// guardar y pisaría el importe justo guardado) — separado del clamp de
	// arriba para no pelearse con él.
	useEffect(() => {
		if (isOpen) {
			setAmount(startingAmount);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// `pausedValue` se pasa explícito (no se lee del estado `paused`) porque
	// "Guardar" y "Pausar" son botones distintos que deciden el valor en el
	// momento del click — leerlo del estado daría un valor de un render atrás.
	// Qué se le pide al equipo depende de la ACCIÓN, no del formulario: dar de
	// alta, cambiar el importe, pausar o reactivar son gestiones distintas en
	// wm.caser.es. Pausar y reactivar no llevan importe.
	const resolveKind = (pausedValue: boolean): WithdrawalKind => {
		if (pausedValue) return WithdrawalKind.ANNUITY_PAUSE;
		if (!currentConfig) return WithdrawalKind.ANNUITY_START;
		return currentConfig.paused ? WithdrawalKind.ANNUITY_RESUME : WithdrawalKind.ANNUITY_UPDATE;
	};

	const handleSave = async (pausedValue: boolean) => {
		if (!token || !userEppConfigId) {
			setSubmitError(translate('rescue.solicitarDialog.errorNoPlan'));
			return;
		}

		setSubmitting(true);
		setSubmitError(null);

		const kind = resolveKind(pausedValue);

		try {
			await createWithdrawalRequest(token, {
				userEppConfigId,
				reason: reason as WithdrawalReason,
				kind,
				// Pausar/reactivar no llevan importe: el backend los rechaza si va.
				...(kind === WithdrawalKind.ANNUITY_START || kind === WithdrawalKind.ANNUITY_UPDATE
					? { monthlyAmount: amount }
					: {})
			});

			onSave({ amount, day: RENTA_MENSUAL_DAY, paused: pausedValue });
			onSubmitted?.();
			setPaused(pausedValue);
			setStep('submitted');
		} catch (caught) {
			setSubmitError(translateWithdrawalError(caught, translate));
		} finally {
			setSubmitting(false);
		}
	};

	// "Guardar"/"Reactivar" son la misma acción (paused=false); "Pausar" es la
	// única que deja paused=true.
	const handleClickSave = () => handleSave(false);

	const handleClickPause = () => handleSave(true);

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto thin-scrollbar">
				<div className="individual-v2 -m-6 p-6 rounded-lg">
					<DialogHeader>
						<DialogTitle className="font-heading">
							{translate('rescue.rentaDialog.title')}
						</DialogTitle>
					</DialogHeader>

					{step === 'submitted' ? (
						<RescueRequestSubmittedV2 onClose={onClose} title={submittedTitle} />
					) : (
						<div className="space-y-5 mt-2">
							<p className="text-sm text-muted-foreground">
								{translate('rescue.rentaDialog.availablePrefix')}{' '}
								<span className="font-semibold text-foreground">{fmtEur(effectiveAvailable)}</span>
								{reason === 'seniority' && seniorityAmount != null && (
									<span className="block text-xs mt-0.5">
										{translate('rescue.rentaDialog.seniorityNote')}
									</span>
								)}
							</p>

							{isCurrentlyPaused && (
								<div className="rounded-lg border border-border p-3 flex items-start gap-2">
									<Clock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
									<p className="text-xs text-muted-foreground">
										{translate('rescue.rentaDialog.pausedNotice')}
									</p>
								</div>
							)}

							<hr className="border-border" />

							<div className="space-y-1.5">
								<Label htmlFor="renta-reason">{translate('rescue.rentaDialog.reasonLabel')}</Label>
								<Select value={reason} onValueChange={setReason} disabled={isReasonLocked}>
									<SelectTrigger id="renta-reason">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{/* Todas las contingencias entran aquí (decisión de producto,
										    ago-2026): al final siempre se contacta manualmente con el
										    usuario por Slack, así que no hace falta limitar el desplegable
										    a los motivos que cortan aportación. Solo se excluyen enfermedad
										    grave y desempleo de larga duración por restricción LEGAL (RD
										    304/2004 art. 9: solo se pueden rescatar en forma de capital). */}
										{RESCUE_REASONS.filter((r) => r.allowsRenta).map((r) => (
											<SelectItem key={r.value} value={r.value}>
												{translate(r.labelKey)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{isReasonLocked && (
									<p className="text-xs text-muted-foreground">
										{translate('rescue.rentaDialog.reasonLockedNote')}
									</p>
								)}
							</div>

							<hr className="border-border" />

							<div className="space-y-1.5">
								<div className="flex items-baseline justify-between">
									<Label>{translate('rescue.rentaDialog.amountLabel')}</Label>
									<span className="text-2xl font-bold font-heading text-primary tracking-tight">
										{fmtNum(amount, 0)}€
									</span>
								</div>
								{/* Reactivar no lleva importe: el backend lo rechaza si va. Dejar
								    el slider suelto era pedirle que eligiera un número que
								    tirábamos por el camino, sin decírselo. */}
								<Slider
									className={cn(
										'slider-arca',
										!hasAdjustedAmount &&
											!isCurrentlyPaused &&
											'[&_[role=slider]]:animate-invite-pulse'
									)}
									value={[amount]}
									onValueChange={([v]) => {
										setAmount(v);
										setHasAdjustedAmount(true);
									}}
									min={MIN_MONTHLY_RENTA}
									max={sliderMax}
									step={10}
									disabled={isCurrentlyPaused}
								/>
								<div className="flex justify-between text-xs text-muted-foreground">
									<span>
										{translate('rescue.rentaDialog.minPrefix')} {fmtNum(MIN_MONTHLY_RENTA, 0)}€
									</span>
									<span>{fmtNum(sliderMax, 0)}€</span>
								</div>
							</div>

							{/* Día fijo por Caser — informativo, no seleccionable. */}
							<div className="rounded-lg border border-border p-3 flex items-start gap-2">
								<Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
								<p className="text-xs text-muted-foreground">
									{translate('rescue.rentaDialog.dayInfo').replace(
										'{{day}}',
										String(RENTA_MENSUAL_DAY)
									)}
								</p>
							</div>

							{daysUntilNextRentaDay() <= RENTA_ALERT_WINDOW_DAYS && (
								<div className="rounded-lg bg-[#ffeff2] border border-[#feccd6] p-3 flex items-start gap-2">
									<AlertTriangle
										className="w-4 h-4 text-[#d4112b] mt-0.5 shrink-0"
										aria-hidden="true"
									/>
									<p className="text-xs text-[#d4112b]">
										{translate('rescue.rentaDialog.deadlineAlert')
											.replace('{{days}}', String(RENTA_ALERT_WINDOW_DAYS))
											.replace('{{day}}', String(RENTA_MENSUAL_DAY))}
									</p>
								</div>
							)}

							<hr className="border-border" />

							{submitError && (
								<p role="alert" className="text-[13px] text-destructive text-center mb-2">
									{submitError}
								</p>
							)}

							<div className="flex flex-col sm:flex-row gap-3">
								<Button className="flex-1 gap-2" onClick={handleClickSave} disabled={submitting}>
									<Sparkles className="w-4 h-4" />
									{isCurrentlyPaused
										? translate('rescue.rentaDialog.reactivateButton')
										: translate('rescue.rentaDialog.saveButton')}
								</Button>
								{/* Pausar solo tiene sentido si ya hay una renta activa que
								    detener — si nunca se ha configurado nada, o ya está pausada
								    (lo que toca aquí es reactivar, no pausar de nuevo), no
								    aplica. */}
								{currentConfig && !isCurrentlyPaused && (
									<Button
										type="button"
										variant="outline"
										className="gap-2 sm:w-auto"
										onClick={handleClickPause}
										disabled={submitting}
									>
										<Pause className="w-4 h-4" />
										{translate('rescue.rentaDialog.pauseButton')}
									</Button>
								)}
							</div>
							<RescueSupportNoteV2 />
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default ConfigurarRentaMensualDialogV2;
